// Stellarium Web - Copyright (c) 2018 - Noctua Software Ltd
//
// This program is licensed under the terms of the GNU AGPL v3, or
// alternatively under a commercial licence.
//
// The terms of the AGPL v3 license can be found in the main directory of this
// repository.

<template>
  <v-card v-if="selectedObject" transparent style="background: rgba(66, 66, 66, 0.6);">
    <v-btn icon style="position: absolute; right: 0" v-on:click.native="toggle()"><v-icon>{{ foldingIcon }}</v-icon></v-btn>
    <v-card-title primary-title>
      <div style="width: 100%">
        <img :src="icon" height="48" width="48" style="margin-top: 3px; margin-right: 10px; float: left" alt="" />
        <div style="overflow: hidden; text-overflow: ellipsis;">
          <div class="text-h5">{{ title }}</div>
          <div class="grey--text text-body-2">{{ type }}</div>
        </div>
      </div>
    </v-card-title>
    <v-expand-transition>
      <v-card
        v-if="reveal"  transparent style="background: rgba(66, 66, 66, 0.0);"
        class="transition-fast-in-fast-out v-card--reveal"
      >
        <v-card-text style="padding-bottom: 5px;">
          <v-row v-if="otherNames.length > 1" style="width: 100%;">
            <v-col cols="4" style="color: #dddddd">{{ $t('Also known as') }}</v-col>
            <v-col cols="8">
              <span class="text-caption white--text" v-for="mname in otherNames1to3" :key="mname" style="margin-right: 15px; font-weight: 500;"><nobr>{{ mname }}</nobr></span>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text>
          <template v-for="item in items">
            <v-row style="width: 100%" :key="item.key" no-gutters>
              <v-col cols="4" style="color: #dddddd">{{ item.key }}</v-col>
              <v-col :cols=" item.cols ? item.cols : 8" style="font-weight: 500" class="white--text"><span v-html="item.value"></span></v-col>
            </v-row>
          </template>
          <div style="margin-top: 15px" class="white--text" v-html="wikipediaSummary"></div>
        </v-card-text>
        <v-card-actions style="margin-top: -25px">
          <v-spacer/>
          <template v-for="item in pluginsSelectedInfoExtraGuiComponents">
            <component :is="item" :key="item"></component>
          </template>
        </v-card-actions>
        <div v-if="$store.state.showSelectedInfoButtons" style="position: absolute; right: 0; bottom: -50px;">
          <v-btn v-if="showPointToButton" fab small color="transparent" v-on:click.native="lockToSelection()">
            <img src="@/assets/images/svg/ui/point_to.svg" height="40px" style="min-height: 40px" alt="" />
          </v-btn>
          <v-btn v-if="!showPointToButton" fab small color="transparent" @mousedown="zoomOutButtonClicked()">
            <img :class="{bt_disabled: !zoomOutButtonEnabled}" src="@/assets/images/svg/ui/remove_circle_outline.svg" height="40px" style="min-height: 40px" alt="" />
          </v-btn>
          <v-btn v-if="!showPointToButton" fab small color="transparent" @mousedown="zoomInButtonClicked()">
            <img :class="{bt_disabled: !zoomInButtonEnabled}" src="@/assets/images/svg/ui/add_circle_outline.svg" height="40px" style="min-height: 40px" alt="" />
          </v-btn>
        </div>
      </v-card>
    </v-expand-transition>
  </v-card>
</template>

<script>

import Moment from 'moment'
import swh from '@/assets/sw_helpers.js'

export default {
  data: function () {
    return {
      wikipediaData: undefined,
      reveal: true,
      isMobile: window.innerWidth < 768,
      foldingIcon: 'mdi-unfold-less-horizontal',
      items: []
    }
  },
  computed: {
    selectedObject: function () {
      return this.$store.state.selectedObject
    },
    stelSelectionId: function () {
      return this.$store.state.stel && this.$store.state.stel.selection ? this.$store.state.stel.selection : undefined
    },
    title: function () {
      return this.selectedObject ? (this.selectedObject.custom ? this.selectedObject.custom.name : this.otherNames[0]) : 'Selection'
    },
    otherNames: function () {
      return this.selectedObject ? swh.namesForSkySource(this.selectedObject, 26) : undefined
    },
    otherNames1to3: function () {
      if (this.selectedObject.custom) {
        if (this.selectedObject.custom.star) {
          return ['HIP ' + this.selectedObject.custom.star.hip].concat(this.otherNames.slice(0, 3))
        }
        return this.otherNames.slice(0, 3)
      } else {
        return this.otherNames.slice(1, 4)
      }
    },
    wikipediaSummary: function () {
      if (!this.wikipediaData) return ''
      const page = this.wikipediaData.query.pages[Object.keys(this.wikipediaData.query.pages)[0]]
      if (!page || !page.extract) return ''
      const wl = '<b><a style="color: #62d1df;" target="_blank" rel="noopener" href="' + this.wikipediaLink + '">wikipedia</a></b></i>'
      return page.extract.replace(/<p>/g, '').replace(/<\/p>/g, '') + '<span class="grey--text text-caption" style="margin-left:auto; margin-right:0;"><i>&nbsp; ' + this.$t('more on {0}', [wl]) + '</span>'
    },
    wikipediaLink: function () {
      const page = this.wikipediaData.query.pages[Object.keys(this.wikipediaData.query.pages)[0]]
      if (!page || !page.extract) return ''
      return 'https://en.wikipedia.org/wiki/' + page.title
    },
    type: function () {
      if (!this.selectedObject) return this.$t('Unknown')
      let morpho = ''
      if (this.selectedObject.model_data && this.selectedObject.model_data.morpho) {
        morpho = swh.nameForGalaxyMorpho(this.selectedObject.model_data.morpho)
        if (morpho) {
          morpho = morpho + ' '
        }
      }
      return morpho + swh.nameForSkySourceType(this.selectedObject.types ? this.selectedObject.types[0] : '')
    },
    icon: function () {
      return swh.iconForSkySource(this.selectedObject)
    },
    showPointToButton: function () {
      if (!this.$store.state.stel.lock) return true
      return this.$store.state.stel.lock !== this.$store.state.stel.selection
    },
    zoomInButtonEnabled: function () {
      return !(!this.$store.state.stel.lock || !this.selectedObject)
    },
    zoomOutButtonEnabled: function () {
      return !(!this.$store.state.stel.lock || !this.selectedObject)
    },
    extraButtons: function () {
      return swh.selectedObjectExtraButtons
    },
    pluginsSelectedInfoExtraGuiComponents: function () {
      let res = []
      for (const i in this.$stellariumWebPlugins()) {
        const plugin = this.$stellariumWebPlugins()[i]
        if (plugin.selectedInfoExtraGuiComponents) {
          res = res.concat(plugin.selectedInfoExtraGuiComponents)
        }
      }
      return res
    }
  },
  watch: {
    reveal: function (newValue) {
      this.foldingIcon = newValue ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal'
    },
    selectedObject: function (s) {
      this.wikipediaData = undefined
      if (!s) {
        if (this.timer) clearInterval(this.timer)
        this.timer = undefined
        return
      }
      const that = this
      that.items = that.computeItems()
      if (that.timer) clearInterval(that.timer)
      that.timer = setInterval(() => { that.items = that.computeItems() }, 1000)

      swh.getSkySourceSummaryFromWikipedia(s).then(data => {
        that.wikipediaData = data
      }, () => { })
    },
    stelSelectionId: function (s) {
      if (!this.$stel.core.selection) {
        this.$store.commit('setSelectedObject', 0)
        return
      }
      this.revealLayer()
      let selectedSwe
      if (this.$stel.core.selection2 && this.$stel.core.selection2.relatedCustom && (this.$stel.core.selection2.relatedCustom.jsonData.model_data.plx === (this.$stel.core.selection.jsonData.model_data ? this.$stel.core.selection.jsonData.model_data.plx : false))) {
        selectedSwe = this.$stel.core.selection2
      } else {
        selectedSwe = this.$stel.core.selection
      }
      swh.sweObj2SkySource(selectedSwe).then(res => {
        this.$store.commit('setSelectedObject', res)
        if (selectedSwe.relatedCustom) {
          this.$stel.core.lock = selectedSwe.relatedCustom
          // Smooth move to selection.
          this.$stel.pointAndLock(selectedSwe.relatedCustom)
          this.$stel.lookAt(selectedSwe.relatedCustom)
        }
      }, err => {
        console.log("Couldn't find info for object " + s + ':' + err)
        this.$store.commit('setSelectedObject', 0)
      })
    }
  },
  methods: {
    revealLayer: function () {
      this.reveal = !this.isMobile
    },
    computeItems: function () {
      const obj = this.$stel.core.selection
      if (!obj) return []
      const that = this

      const ret = []

      const addAttr = (key, attr, format) => {
        const v = obj.getInfo(attr)
        if (v && !isNaN(v)) {
          ret.push({
            key: key,
            value: format ? format(v) : v.toString()
          })
        }
      }
      if (this.selectedObject.custom) {
        ret.push({
          key: that.$t('Reg.-No.'),
          value: this.selectedObject.custom.registration_number.toString()
        })
        if (this.selectedObject.custom.date) {
          ret.push({
            key: that.$t('Dedication Date'),
            value: this.selectedObject.custom.date.toString()
          })
        }
        if (this.selectedObject.custom.dedication) {
          ret.push({
            key: that.$t('Dedication Text'),
            value: '<div class="dedication">' + this.selectedObject.custom.dedication.toString() + '</div>',
            cols: 12
          })
        } else {
          // empty item to keep spacing
          ret.push({
            key: '',
            value: '<div class="dedication"></div>',
            cols: 12
          })
        }
      }

      addAttr(that.$t('Magnitude'), 'vmag', this.formatMagnitude)
      if (this.selectedObject.model_data) {
        if (this.selectedObject.model_data.radius) {
          ret.push({
            key: that.$t('Radius'),
            value: this.selectedObject.model_data.radius.toString() + ' Km'
          })
        }
        if (this.selectedObject.model_data.dimx) {
          const dimy = this.selectedObject.model_data.dimy ? this.selectedObject.model_data.dimy : this.selectedObject.model_data.dimx
          ret.push({
            key: that.$t('Size'),
            value: this.selectedObject.model_data.dimx.toString() + "' x " + dimy.toString() + "'"
          })
        }
      }
      const formatInt = function (num, padLen) {
        const pad = new Array(1 + padLen).join('0')
        return (pad + num).slice(-pad.length)
      }
      const formatRA = function (a) {
        const raf = that.$stel.a2tf(a, 1)
        return '<div class="radecVal">' + formatInt(raf.hours, 2) + '<span class="radecUnit">h</span>&nbsp;</div><div class="radecVal">' + formatInt(raf.minutes, 2) + '<span class="radecUnit">m</span></div><div class="radecVal">' + formatInt(raf.seconds, 2) + '.' + raf.fraction + '<span class="radecUnit">s</span></div>'
      }
      const formatDec = function (a) {
        const raf = that.$stel.a2af(a, 1)
        return '<div class="radecVal">' + raf.sign + formatInt(raf.degrees, 2) + '<span class="radecUnit">°</span></div><div class="radecVal">' + formatInt(raf.arcminutes, 2) + '<span class="radecUnit">\'</span></div><div class="radecVal">' + formatInt(raf.arcseconds, 2) + '.' + raf.fraction + '<span class="radecUnit">"</span></div>'
      }
      const posCIRS = this.$stel.convertFrame(this.$stel.core.observer, 'ICRF', 'JNOW', obj.getInfo('radec'))
      const radecCIRS = this.$stel.c2s(posCIRS)
      const raCIRS = this.$stel.anp(radecCIRS[0])
      const decCIRS = this.$stel.anpm(radecCIRS[1])
      ret.push({
        key: that.$t('Ra/Dec'),
        value: formatRA(raCIRS) + '&nbsp;&nbsp;&nbsp;' + formatDec(decCIRS)
      })
      addAttr(that.$t('Phase'), 'phase', this.formatPhase)
      const vis = obj.computeVisibility()
      let str
      if (vis.length === 0) {
        str = that.$t('Not visible tonight')
      } else if (vis[0].rise === null) {
        str = that.$t('Always visible tonight')
      } else {
        str = that.$t('Rise: {0}&nbsp;&nbsp;&nbsp; Set: {1}', [this.formatTime(vis[0].rise), this.formatTime(vis[0].set)])
      }
      ret.push({
        key: that.$t('Visibility'),
        value: str
      })
      return ret
    },
    formatPhase: function (v) {
      return (v * 100).toFixed(0) + '%'
    },
    formatMagnitude: function (v) {
      if (!v) {
        return 'Unknown'
      }
      return v.toFixed(2)
    },
    formatDistance: function (d) {
      // d is in AU
      if (!d) {
        return 'NAN'
      }
      const ly = d * swh.astroConstants.ERFA_AULT / swh.astroConstants.ERFA_DAYSEC / swh.astroConstants.ERFA_DJY
      if (ly >= 0.1) {
        return ly.toFixed(2) + '<span class="radecUnit"> light years</span>'
      }
      if (d >= 0.1) {
        return d.toFixed(2) + '<span class="radecUnit"> AU</span>'
      }
      const meter = d * swh.astroConstants.ERFA_DAU
      if (meter >= 1000) {
        return (meter / 1000).toFixed(2) + '<span class="radecUnit"> km</span>'
      }
      return meter.toFixed(2) + '<span class="radecUnit"> m</span>'
    },
    formatTime: function (jdm) {
      const d = new Date()
      d.setMJD(jdm)
      const utc = new Moment(d)
      utc.utcOffset(this.$store.state.stel.utcoffset)
      return utc.format('HH:mm')
    },
    unselect: function () {
      this.$stel.core.selection = 0
    },
    toggle: function () {
      this.reveal = !this.reveal
    },
    lockToSelection: function () {
      if (this.$stel.core.selection) {
        this.$stel.pointAndLock(this.$stel.core.selection, 0.5)
      }
    },
    zoomInButtonClicked: function () {
      const currentFov = this.$store.state.stel.fov * 180 / Math.PI
      this.$stel.zoomTo(currentFov * 0.3 * Math.PI / 180, 0.4)
      const that = this
      this.zoomTimeout = setTimeout(_ => { that.zoomInButtonClicked() }, 300)
    },
    zoomOutButtonClicked: function () {
      const currentFov = this.$store.state.stel.fov * 180 / Math.PI
      this.$stel.zoomTo(currentFov * 3 * Math.PI / 180, 0.6)
      const that = this
      this.zoomTimeout = setTimeout(_ => { that.zoomOutButtonClicked() }, 200)
    },
    stopZoom: function () {
      if (this.zoomTimeout) {
        clearTimeout(this.zoomTimeout)
        this.zoomTimeout = undefined
      }
    },
    extraButtonClicked: function (btn) {
      btn.callback()
    }
  },
  mounted: function () {
    const that = this
    that.revealLayer()
    window.addEventListener('mouseup', function () {
      that.stopZoom()
    })
  }
}
</script>

<style>
.bt_disabled {
  filter: opacity(0.2);
}

.radecVal {
  display: inline-block;
  font-family: monospace;
  padding-right: 2px;
  font-size: 13px;
  font-weight: bold;
}

.radecUnit {
  color: #dddddd;
  font-weight: normal
}
.dedication {
  padding: 10px 0;
}
.dedication p {
  margin-bottom: 0px;
}
</style>
